// Used for only global custom JS

(() => {
  const PDF_CONTEXT_CLASS = 'pdf-link-context';
  const DECORATIVE_SELECTOR = '.sr-only, svg, img, picture, [aria-hidden="true"]';
  const PDF_SUFFIX = ' (PDF)';
  const NEW_TAB_TEXT = ', opens in new tab';
  const LANTERN_QUIZ_HASH = '#open-quiz-tools/perfect-product-finder/shelf-awareness-quiz';
  const LANTERN_QUIZ_TRIGGER_SELECTOR = `[data-lantern-quiz-trigger], a[href="${LANTERN_QUIZ_HASH}"]`;
  const LANTERN_QUIZ_IFRAME_SELECTOR = 'iframe[src*="/tools/perfect-product-finder/shelf-awareness-quiz"]';
  const DISMISS_CONTROL_SELECTOR = 'button, [role="button"], a[href], [tabindex], [data-close]';
  const CLOSE_TEXT_MATCHER = /\b(close|dismiss)\b|^[×x]$/i;

  let lastLanternQuizTrigger = null;

  function isPdfHref(href) {
    return /\.pdf(?:$|[?#])/i.test(href || '');
  }

  function getVisibleLinkText(anchor) {
    const clone = anchor.cloneNode(true);
    clone.querySelectorAll(DECORATIVE_SELECTOR).forEach((element) => element.remove());
    return clone.textContent.replace(/\s+/g, ' ').trim();
  }

  function insertBeforeDecorative(anchor, node) {
    anchor.insertBefore(node, anchor.querySelector(DECORATIVE_SELECTOR));
  }

  function ensureVisiblePdfText(anchor, visibleText) {
    if (!visibleText || /\bpdf\b/i.test(visibleText)) return;
    insertBeforeDecorative(anchor, document.createTextNode(PDF_SUFFIX));
  }

  function ensureNewTabContext(anchor) {
    if (anchor.target !== '_blank') return;
    if (anchor.querySelector(`.${PDF_CONTEXT_CLASS}`)) return;

    const context = document.createElement('span');
    context.className = `sr-only ${PDF_CONTEXT_CLASS}`;
    context.textContent = NEW_TAB_TEXT;
    insertBeforeDecorative(anchor, context);
  }

  function ensureAriaLabel(anchor, href, visibleText) {
    const originalLabel = anchor.getAttribute('aria-label');
    if (!originalLabel && visibleText) return;

    let label = originalLabel;

    if (!label) {
      label = decodeURIComponent((href || '').split('/').pop()?.split(/[?#]/)[0] || 'PDF');
      label = label.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim() || 'PDF';
    }

    if (!/\bpdf\b/i.test(label)) {
      label += ', PDF';
    }

    if (anchor.target === '_blank' && !/opens in new tab/i.test(label)) {
      label += NEW_TAB_TEXT;
    }

    if (label !== originalLabel) {
      anchor.setAttribute('aria-label', label);
    }
  }

  function enhancePdfLink(anchor) {
    if (anchor.hasAttribute('data-a11y-download')) return;

    const href = anchor.getAttribute('href');
    if (!isPdfHref(href)) return;

    const visibleText = getVisibleLinkText(anchor);
    ensureVisiblePdfText(anchor, visibleText);
    ensureAriaLabel(anchor, href, visibleText);

    if (!anchor.hasAttribute('aria-label')) {
      ensureNewTabContext(anchor);
    }
  }

  function enhancePdfLinks(root = document) {
    if (root instanceof HTMLAnchorElement) {
      enhancePdfLink(root);
      return;
    }

    root.querySelectorAll('a[href]').forEach(enhancePdfLink);
  }

  function initPdfLinkAccessibility() {
    enhancePdfLinks(document);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) enhancePdfLinks(node);
          });
        }

        if (mutation.type === 'attributes' && mutation.target instanceof HTMLAnchorElement) {
          enhancePdfLink(mutation.target);
        }
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'target', 'aria-label']
    });
  }

  function getElementFingerprint(element) {
    return [
      element.id,
      typeof element.className === 'string' ? element.className : '',
      element.getAttribute('name'),
      element.getAttribute('title'),
      element.getAttribute('aria-label'),
      element.textContent?.trim()
    ].join(' ');
  }

  function isVisibleElement(element) {
    if (!element?.isConnected || element.hidden || element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return false;

    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }

  function findLanternQuizIframe() {
    return Array.from(document.querySelectorAll(LANTERN_QUIZ_IFRAME_SELECTOR))
      .find((iframe) => iframe instanceof HTMLIFrameElement && isVisibleElement(iframe));
  }

  function getLanternQuizContent(iframe) {
    const viewportArea = window.innerWidth * window.innerHeight;
    const iframeRect = iframe.getBoundingClientRect();
    let content = iframe;
    let candidate = iframe.parentElement;

    while (candidate && candidate !== document.body) {
      if (!isVisibleElement(candidate)) {
        candidate = candidate.parentElement;
        continue;
      }

      const rect = candidate.getBoundingClientRect();
      const area = rect.width * rect.height;
      const isViewportOverlay = area >= viewportArea * 0.85 && rect.width >= window.innerWidth * 0.9;
      const containsIframe = (
        rect.left <= iframeRect.left &&
        rect.right >= iframeRect.right &&
        rect.top <= iframeRect.top &&
        rect.bottom >= iframeRect.bottom
      );

      if (!isViewportOverlay && containsIframe) {
        content = candidate;
      }

      candidate = candidate.parentElement;
    }

    return content;
  }

  function getRectDistance(firstRect, secondRect) {
    const firstX = firstRect.left + firstRect.width / 2;
    const firstY = firstRect.top + firstRect.height / 2;
    const secondX = secondRect.left + secondRect.width / 2;
    const secondY = secondRect.top + secondRect.height / 2;

    return Math.hypot(firstX - secondX, firstY - secondY);
  }

  function findLanternQuizDismissButton(iframe, content) {
    const contentRect = content.getBoundingClientRect();
    const candidates = Array.from(document.querySelectorAll(DISMISS_CONTROL_SELECTOR))
      .filter((element) => isVisibleElement(element) && CLOSE_TEXT_MATCHER.test(getElementFingerprint(element)))
      .map((element) => ({
        element,
        distance: getRectDistance(element.getBoundingClientRect(), contentRect)
      }))
      .filter(({ element, distance }) => {
        if (element.closest('iframe')) return false;
        if (content.contains(element)) return true;
        return distance <= Math.max(contentRect.width, contentRect.height, 300);
      })
      .sort((first, second) => first.distance - second.distance);

    return candidates[0]?.element || null;
  }

  function setLanternQuizTriggerExpanded(expanded) {
    document.querySelectorAll(LANTERN_QUIZ_TRIGGER_SELECTOR).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  function closeLanternQuizPopup() {
    const iframe = findLanternQuizIframe();
    if (!iframe) return false;

    const content = getLanternQuizContent(iframe);
    const dismissButton = findLanternQuizDismissButton(iframe, content);
    if (!dismissButton) return false;

    dismissButton.click();
    setLanternQuizTriggerExpanded(false);

    if (lastLanternQuizTrigger?.isConnected) {
      lastLanternQuizTrigger.focus({ preventScroll: true });
    }

    return true;
  }

  function initLanternQuizCloseBehavior() {
    if (window.location.hash === LANTERN_QUIZ_HASH) {
      setLanternQuizTriggerExpanded(true);
    }

    window.addEventListener('hashchange', () => {
      if (window.location.hash !== LANTERN_QUIZ_HASH) return;

      setLanternQuizTriggerExpanded(true);
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.(LANTERN_QUIZ_TRIGGER_SELECTOR);
      if (!trigger) return;

      lastLanternQuizTrigger = trigger;
      setLanternQuizTriggerExpanded(true);
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!closeLanternQuizPopup()) return;

      event.preventDefault();
      event.stopPropagation();
    }, true);
  }

  function fixVideoPosters() {
    document.querySelectorAll('video > img:not([alt])').forEach(function(img) {
      img.setAttribute('alt', '');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initPdfLinkAccessibility();
      initLanternQuizCloseBehavior();
      fixVideoPosters();
    }, { once: true });
  } else {
    initPdfLinkAccessibility();
    initLanternQuizCloseBehavior();
    fixVideoPosters();
  }

  document.addEventListener('page:loaded', () => {
    enhancePdfLinks(document);
    fixVideoPosters();
  });
})();

// Product-card hover carousels: Flickity bullets are native buttons, but Concept
// nests them in the product <a> and hides them until hover. Keep mouse preview,
// and make the visible bullets a single keyboard tab stop.
(() => {
  const SecondaryMedia = customElements.get('secondary-media');
  if (!SecondaryMedia?.prototype?.init) return;

  const originalInit = SecondaryMedia.prototype.init;
  const originalDisconnected = SecondaryMedia.prototype.disconnectedCallback;

  function visibleDots(dotsNav) {
    return Array.from(dotsNav.querySelectorAll('.flickity-page-dot')).filter((dot) => {
      return window.getComputedStyle(dot).display !== 'none';
    });
  }

  function syncDotRoving(dotsNav) {
    const allDots = Array.from(dotsNav.querySelectorAll('.flickity-page-dot'));
    const dots = visibleDots(dotsNav);
    if (dots.length === 0) return;

    const current = dots.find((dot) => dot.classList.contains('is-selected')) || dots[0];
    allDots.forEach((dot) => {
      dot.tabIndex = dot === current ? 0 : -1;
    });
  }

  function labelForDot(host, index) {
    const template = host.getAttribute('data-dot-label');
    if (template) return template.replaceAll('__n__', String(index + 1));
    return `View slide ${index + 1}`;
  }

  function enhanceDots(host) {
    if (!host.carousel || host.classList.contains('without-dots')) return;

    const dotsNav = host.querySelector('.flickity-page-dots');
    if (!dotsNav || host._a11yDotsNav === dotsNav) return;

    host._a11yDotsNav = dotsNav;
    const allDots = Array.from(dotsNav.querySelectorAll('.flickity-page-dot'));
    allDots.forEach((dot, index) => {
      dot.setAttribute('aria-label', labelForDot(host, index));
    });

    dotsNav.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const dots = visibleDots(dotsNav);
      const currentIndex = dots.indexOf(event.target.closest('.flickity-page-dot'));
      if (currentIndex === -1 || dots.length < 2) return;

      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const prevKey = rtl ? 'ArrowRight' : 'ArrowLeft';
      const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';

      let nextIndex = currentIndex;
      if (event.key === prevKey) nextIndex = (currentIndex - 1 + dots.length) % dots.length;
      else if (event.key === nextKey) nextIndex = (currentIndex + 1) % dots.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = dots.length - 1;

      event.preventDefault();
      const nextDot = dots[nextIndex];
      const slideIndex = allDots.indexOf(nextDot);
      if (slideIndex === -1) return;

      host.carousel.select(slideIndex);
      nextDot.focus();
    });

    host.carousel.on('select', () => syncDotRoving(dotsNav));
    syncDotRoving(dotsNav);
  }

  function bindMediaHover(host) {
    host.removeEventListener('mousemove', host.onMoveHandler);
    host.removeEventListener('mouseleave', host.onLeaveHandler);

    const media = host.closest('.product-card__media');
    if (!media || !host.carousel) return;

    host._cardMedia = media;
    host._boundMediaMove = (event) => {
      if (host.matches(':focus-within')) return;
      if (event.target.closest('.flickity-page-dot')) return;
      host.onMoveHandler(event);
    };
    host._boundMediaLeave = host.onLeaveHandler.bind(host);
    media.addEventListener('mousemove', host._boundMediaMove);
    media.addEventListener('mouseleave', host._boundMediaLeave);
  }

  function unbindMediaHover(host) {
    if (!host._cardMedia) return;

    host._cardMedia.removeEventListener('mousemove', host._boundMediaMove);
    host._cardMedia.removeEventListener('mouseleave', host._boundMediaLeave);
    host._cardMedia = null;
    host._boundMediaMove = null;
    host._boundMediaLeave = null;
  }

  SecondaryMedia.prototype.init = function patchedSecondaryMediaInit() {
    originalInit.call(this);
    if (!this.carousel) return;

    bindMediaHover(this);
    enhanceDots(this);
  };

  SecondaryMedia.prototype.disconnectedCallback = function patchedSecondaryMediaDisconnected() {
    unbindMediaHover(this);
    if (typeof originalDisconnected === 'function') originalDisconnected.call(this);
  };
})();

// Shelfology Custom: announce product quantity plus/minus updates for screen readers (WCAG 4.1.3).
(() => {
  const QUANTITY_ANNOUNCE_DELAY = 50;
  const QUANTITY_ANNOUNCE_CLEAR_DELAY = 5000;
  const quantityAnnounceTimers = new WeakMap();

  function announceQuantityChange(status, quantity) {
    const previousTimers = quantityAnnounceTimers.get(status);
    if (previousTimers) {
      window.clearTimeout(previousTimers.setTimeoutId);
      window.clearTimeout(previousTimers.clearTimeoutId);
    }

    status.textContent = '';
    const message = (status.getAttribute('data-status-template') || '').replaceAll('__QUANTITY__', String(quantity));
    if (!message) {
      quantityAnnounceTimers.delete(status);
      return;
    }

    const timers = { setTimeoutId: null, clearTimeoutId: null };
    timers.setTimeoutId = window.setTimeout(() => {
      status.textContent = message;
      timers.setTimeoutId = null;
      timers.clearTimeoutId = window.setTimeout(() => {
        if (status.textContent === message) {
          status.textContent = '';
        }

        quantityAnnounceTimers.delete(status);
      }, QUANTITY_ANNOUNCE_CLEAR_DELAY);
    }, QUANTITY_ANNOUNCE_DELAY);

    quantityAnnounceTimers.set(status, timers);
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('quantity__input')) {
      return;
    }

    const host = input.closest('quantity-input.quantity');
    const status = host && host.querySelector('[data-quantity-status]');
    if (!status) {
      return;
    }

    announceQuantityChange(status, input.value);
  });
})();
// Shelfology Custom: Add-to-Cart info weight and Shop Pay help control handling.
(() => {
  const GRAMS_TO_POUND_HUNDREDTHS = 0.220462;
  const SHOP_PAY_HELP_SELECTOR = [
    '.shopify-installments__learn-more',
    '[class*="learn-more" i]',
    '[class*="LearnMore"]',
    '[class*="info-icon" i]',
    '[aria-label*="Learn more" i]',
  ].join(', ');

  // Number only; the unit lives in the Liquid markup. Rounds via hundredths to match the Liquid first paint.
  function formatVariantWeightPounds(grams) {
    return (Math.round(Number(grams) * GRAMS_TO_POUND_HUNDREDTHS) / 100).toFixed(2);
  }

  function cssEscape(value) {
    const text = String(value);
    if (window.CSS?.escape) return CSS.escape(text);
    return text.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function syncWeightRowTitle(row) {
    const title = row.querySelector('[data-add-to-cart-info-weight-title]');
    const summary = row.querySelector('.custom-add-to-cart-info__summary');
    if (!title || !summary) return;
    title.textContent = summary.textContent.replace(/\s+/g, ' ').trim();
  }

  function applyWeightToExistingRows(root, variant) {
    const grams = Number(variant.weight) || 0;
    const formatted = formatVariantWeightPounds(grams);
    const hasWeight = grams > 0;

    root.querySelectorAll('[data-add-to-cart-info-weight-row]').forEach((row) => {
      row.hidden = !hasWeight;
      row.querySelectorAll('[data-add-to-cart-info-weight]').forEach((element) => {
        element.textContent = formatted;
      });
      syncWeightRowTitle(row);
    });
  }

  function replaceWeightRowsFromSection(root, source) {
    const list = root.querySelector('.custom-add-to-cart-info__list');
    if (!list) return false;

    const sourceRows = Array.from(source.querySelectorAll('[data-add-to-cart-info-weight-row]'));
    if (!sourceRows.length && !root.querySelector('[data-add-to-cart-info-weight-row]')) {
      return false;
    }

    sourceRows.forEach((sourceRow) => {
      const imported = document.importNode(sourceRow, true);
      const current = sourceRow.id ? root.querySelector(`#${cssEscape(sourceRow.id)}`) : null;
      if (current) {
        current.replaceWith(imported);
        return;
      }

      const next = sourceRow.nextElementSibling;
      const destNext = next?.id ? root.querySelector(`#${cssEscape(next.id)}`) : null;
      if (destNext) destNext.before(imported);
      else list.append(imported);
    });

    root.querySelectorAll('[data-add-to-cart-info-weight-row]').forEach((row) => {
      if (row.id && !source.querySelector(`#${cssEscape(row.id)}`)) row.remove();
    });
    return true;
  }

  function updateAddToCartInfoWeight(eventData) {
    const productId = eventData?.data?.productId;
    const sectionId = eventData?.data?.sectionId;
    const variant = eventData?.data?.variant;
    if (!productId || !sectionId || !variant) return;

    const root = document.querySelector(
      `[data-add-to-cart-info][data-section-id="${cssEscape(sectionId)}"][data-product-id="${cssEscape(productId)}"]`
    );
    if (!root) return;

    const parsedHTML = eventData.data.parsedHTML;
    const source = parsedHTML?.querySelector?.(
      `[data-add-to-cart-info][data-section-id="${cssEscape(sectionId)}"][data-product-id="${cssEscape(productId)}"]`
    );
    if (source && replaceWeightRowsFromSection(root, source)) return;

    applyWeightToExistingRows(root, variant);
  }

  function isShopPayDialogControl(element) {
    return Boolean(element.closest('[role="dialog"], dialog'));
  }

  function isShopPayBannerHelpControl(element) {
    if (!element || isShopPayDialogControl(element)) return false;

    const label = `${element.getAttribute('aria-label') || ''} ${element.getAttribute('title') || ''} ${element.textContent || ''}`.trim();
    if (/learn more/i.test(label)) return true;
    if (/view sample plans/i.test(label)) return true;
    if (/^\s*\?\s*$/.test(element.textContent || '')) return true;
    if (element.matches(SHOP_PAY_HELP_SELECTOR)) return true;
    return element.matches('button, [role="button"]');
  }

  const SHOP_PAY_HELP_STYLE = `
      :host {
        display: block;
        position: static;
        width: 100%;
        height: auto;
      }
      #shopify-installments {
        box-sizing: border-box;
        width: 100%;
        margin: 0;
        padding: 0;
        overflow-wrap: anywhere;
      }
      #shopify-installments p {
        margin: 0;
      }
      #shopify-installments,
      #shopify-installments *:not(svg, svg *) {
        font-family: var(--atc-shop-pay-font-family, inherit);
        font-size: var(--atc-shop-pay-font-size, inherit);
        line-height: var(--atc-shop-pay-line-height, inherit);
      }
      #shopify-installments,
      #shopify-installments p {
        color: var(--atc-shop-pay-color, inherit);
      }
      button:not([role="dialog"] button):not(dialog button),
      [role="button"]:not([role="dialog"] [role="button"]):not(dialog [role="button"]) {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        opacity: 0;
        font-size: 0;
        color: transparent;
        background: transparent;
        border: 0;
        cursor: pointer;
        z-index: 1;
      }
    `;
  const SHOP_PAY_WATCHERS = new WeakMap();
  let shopPayLifecycleBound = false;
  let shopPayVariantBound = false;

  function shopPayAccessibleName(terms) {
    const item = terms.closest('[data-shop-pay-label]');
    const label = item?.getAttribute('data-shop-pay-label')?.trim();
    return label || 'Shop Pay Installments';
  }

  function ensureShopPayHelpStyles(root) {
    const styleId = 'atc-hide-shop-pay-help';
    const existing = root.querySelector(`#${styleId}`);
    if (existing) {
      if (existing.textContent !== SHOP_PAY_HELP_STYLE) {
        existing.textContent = SHOP_PAY_HELP_STYLE;
      }
      return;
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = SHOP_PAY_HELP_STYLE;
    root.appendChild(style);
  }

  function hideShopPayHelpIcon(terms) {
    const root = terms.shadowRoot;
    if (!root) return false;

    ensureShopPayHelpStyles(root);
    const name = shopPayAccessibleName(terms);
    root.querySelectorAll('button, a, [role="button"]').forEach((element) => {
      if (!isShopPayBannerHelpControl(element)) return;
      if (element.getAttribute('aria-label') !== name) {
        element.setAttribute('aria-label', name);
      }
      if (element.getAttribute('aria-haspopup') !== 'dialog') {
        element.setAttribute('aria-haspopup', 'dialog');
      }
      if (element.hasAttribute('aria-hidden')) {
        element.removeAttribute('aria-hidden');
      }
      if (element.tabIndex !== 0) {
        element.tabIndex = 0;
      }
      if (element.style.display) {
        element.style.removeProperty('display');
      }
    });
    return true;
  }

  function teardownShopPayHelpIcons(scope) {
    collectShopPayTerms(scope).forEach((terms) => {
      const watch = SHOP_PAY_WATCHERS.get(terms);
      if (!watch) return;
      watch.observer.disconnect();
      watch.timeouts.forEach((id) => window.clearTimeout(id));
      SHOP_PAY_WATCHERS.delete(terms);
    });
  }

  function collectShopPayTerms(scope) {
    const root = scope || document;
    return Array.from(root.querySelectorAll?.('.custom-add-to-cart-info shopify-payment-terms') || []);
  }

  function watchShopPayHelpIcon(terms) {
    if (SHOP_PAY_WATCHERS.has(terms)) return;

    const watch = {
      observer: null,
      timeouts: [],
      shadowObserved: false,
    };

    const hide = () => {
      hideShopPayHelpIcon(terms);
      if (terms.shadowRoot && !watch.shadowObserved) {
        watch.shadowObserved = true;
        watch.observer.observe(terms.shadowRoot, { childList: true, subtree: true });
      }
    };

    watch.observer = new MutationObserver(hide);
    watch.observer.observe(terms, { childList: true, subtree: true });
    SHOP_PAY_WATCHERS.set(terms, watch);
    hide();
    watch.timeouts = [100, 300, 800, 2000].map((delay) => window.setTimeout(hide, delay));
  }

  function initShopPayHelpIcons(scope) {
    collectShopPayTerms(scope).forEach(watchShopPayHelpIcon);
  }

  function bindShopPayLifecycle() {
    if (shopPayLifecycleBound) return;
    shopPayLifecycleBound = true;

    document.addEventListener('shopify:section:load', (event) => {
      initShopPayHelpIcons(event.target);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      teardownShopPayHelpIcons(event.target);
    });
  }

  function onShopPayReady() {
    bindShopPayLifecycle();
    bindShopPayDialogFocus();
    if (!shopPayVariantBound && window.theme?.pubsub?.subscribe && theme.pubsub.PUB_SUB_EVENTS?.variantChange) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (eventData) => {
        updateAddToCartInfoWeight(eventData);
        window.requestAnimationFrame(() => initShopPayHelpIcons());
      });
      shopPayVariantBound = true;
    }

    initShopPayHelpIcons();
    if (window.customElements?.whenDefined) {
      customElements.whenDefined('shopify-payment-terms').then(() => initShopPayHelpIcons());
    }
  }

  // Shelfology Custom: move focus to a visible control in the Shop Pay installment dialog (WCAG 2.4.3).
  // The dialog section is tabindex="-1" with outline suppressed, so focusing it shows no ring.
  const SHOP_PAY_INSTALLMENTS_LABEL = 'Get it now, pay later';
  const SHOP_PAY_DIALOG_CONTROL_SELECTOR = [
    'a[href]',
    'button:enabled',
    'input:not([type="hidden"]):enabled',
    'select:enabled',
    'textarea:enabled',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');
  let shopPayDialogUserControl = null;
  let shopPayDialogMovedByUs = null;
  let shopPayDialogFocusLock = false;
  const SHOP_PAY_DIALOG_WATCHED = new WeakSet();
  const SHOP_PAY_FOCUS_CORRECT_MS = 1500;

  function isElementDisplayed(element) {
    if (!element || !element.isConnected) return false;
    if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function deepActiveElement() {
    let active = document.activeElement;
    while (active?.shadowRoot?.activeElement) {
      active = active.shadowRoot.activeElement;
    }
    return active;
  }

  function isShopPayInstallmentsDialog(node) {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
    const role = (node.getAttribute('role') || '').toLowerCase();
    if (role !== 'dialog' && node.tagName !== 'DIALOG') return false;
    return (node.getAttribute('aria-label') || '') === SHOP_PAY_INSTALLMENTS_LABEL;
  }

  function dialogInShadowRoot(shadowRoot) {
    if (!shadowRoot?.querySelector) return null;
    const labeled = shadowRoot.querySelector(`[aria-label="${SHOP_PAY_INSTALLMENTS_LABEL}"]`);
    if (labeled && isShopPayInstallmentsDialog(labeled) && isElementDisplayed(labeled)) return labeled;
    return null;
  }

  function dialogOnHost(host) {
    if (!host || host.nodeType !== Node.ELEMENT_NODE) return null;
    if (isShopPayInstallmentsDialog(host) && isElementDisplayed(host)) return host;
    return dialogInShadowRoot(host.shadowRoot);
  }

  function findShopPayInstallmentsDialog(fromNode) {
    let node = fromNode;
    while (node) {
      const found = dialogOnHost(node);
      if (found) return found;
      node = node.parentElement || node.host;
    }

    const body = document.body;
    if (!body) return null;
    for (let i = 0; i < body.children.length; i += 1) {
      const found = dialogOnHost(body.children[i]);
      if (found) return found;
    }
    return null;
  }

  function isShopPayDialogTabbable(element) {
    if (!element || element.nodeType !== Node.ELEMENT_NODE) return false;
    if (element.tabIndex < 0 || element.disabled) return false;
    if (element.closest('[inert]')) return false;
    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true });
    }
    return isElementDisplayed(element);
  }

  function shopPayDialogControlName(element) {
    return `${element.getAttribute('aria-label') || ''} ${element.getAttribute('title') || ''} ${element.textContent || ''}`
      .replace(/\s+/g, ' ')
      .trim();
  }

  function preferredShopPayDialogControl(dialog) {
    const controls = Array.from(dialog.querySelectorAll(SHOP_PAY_DIALOG_CONTROL_SELECTOR))
      .filter((element) => element !== dialog && isShopPayDialogTabbable(element));
    return controls.find((element) => /^close\b/i.test(shopPayDialogControlName(element))) || controls[0] || null;
  }

  function shopPayDialogHasUserFocus(dialog, active) {
    return Boolean(active && active !== dialog && dialog.contains(active) && isShopPayDialogTabbable(active));
  }

  function ensureShopPayDialogFocusWatch(dialog) {
    if (!dialog || SHOP_PAY_DIALOG_WATCHED.has(dialog)) return;
    SHOP_PAY_DIALOG_WATCHED.add(dialog);
    dialog.addEventListener('focusin', onShopPayDialogFocusIn);
  }

  function focusShopPayInstallmentsDialog(dialog, allowShell) {
    if (!dialog || !isElementDisplayed(dialog) || shopPayDialogFocusLock) return false;
    ensureShopPayDialogFocusWatch(dialog);
    const active = deepActiveElement();
    if (shopPayDialogHasUserFocus(dialog, active)) return true;

    const remembered = shopPayDialogUserControl;
    const rememberedOk = remembered && dialog.contains(remembered) && isShopPayDialogTabbable(remembered);
    const target = rememberedOk ? remembered : preferredShopPayDialogControl(dialog);
    if (!target) {
      if (!allowShell) return false;
      if (active === dialog) return true;
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });
      return true;
    }
    if (active === target) return true;

    shopPayDialogFocusLock = true;
    try {
      shopPayDialogMovedByUs = target;
      // focusVisible draws the ring after a pointer open; the dialog shell suppresses its own outline.
      target.focus({ preventScroll: true, focusVisible: true });
    } finally {
      shopPayDialogFocusLock = false;
    }
    return true;
  }

  function onShopPayDialogFocusIn(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const dialog = path.find((node) => isShopPayInstallmentsDialog(node));
    if (!dialog || !isElementDisplayed(dialog)) return;

    const active = deepActiveElement();
    if (!shopPayDialogHasUserFocus(dialog, active)) {
      focusShopPayInstallmentsDialog(dialog);
      return;
    }
    // element.focus() still reports a trusted focusin, so ignore the control we just moved to.
    if (active !== shopPayDialogMovedByUs) shopPayDialogUserControl = active;
  }

  function bindShopPayDialogFocus() {
    if (bindShopPayDialogFocus.bound) return;
    bindShopPayDialogFocus.bound = true;

    document.addEventListener('focusin', onShopPayDialogFocusIn, true);

    document.addEventListener(
      'click',
      (event) => {
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        const terms = path.find((node) => node && node.tagName === 'SHOPIFY-PAYMENT-TERMS')
          || event.target.closest?.('shopify-payment-terms');
        if (!terms) return;
        shopPayDialogUserControl = null;
        shopPayDialogMovedByUs = null;

        const tryFocus = (allowShell = false) => {
          for (const node of path) {
            const fromPath = dialogOnHost(node);
            if (fromPath) return focusShopPayInstallmentsDialog(fromPath, allowShell);
          }
          const dialog = findShopPayInstallmentsDialog(terms);
          if (!dialog) return false;
          return focusShopPayInstallmentsDialog(dialog, allowShell);
        };

        const observers = [];
        let stopped = false;
        const stop = () => {
          if (stopped) return;
          stopped = true;
          observers.forEach((observer) => observer.disconnect());
          observers.length = 0;
        };

        const watchRoot = (root) => {
          if (!root || stopped) return;
          const observer = new MutationObserver((mutations) => {
            if (stopped) return;
            for (const mutation of mutations) {
              for (const node of mutation.addedNodes) {
                if (node.nodeType !== Node.ELEMENT_NODE) continue;
                if (node.shadowRoot) watchRoot(node.shadowRoot);
                tryFocus();
              }
            }
          });
          observer.observe(root, { childList: true });
          observers.push(observer);
        };

        watchRoot(document.body);
        if (terms.shadowRoot) watchRoot(terms.shadowRoot);
        for (let i = 0; i < document.body.children.length; i += 1) {
          const child = document.body.children[i];
          if (child.shadowRoot) watchRoot(child.shadowRoot);
        }

        const startedAt = performance.now();
        const poll = () => {
          const dialog = findShopPayInstallmentsDialog(terms);
          if (dialog) ensureShopPayDialogFocusWatch(dialog);
          const active = deepActiveElement();
          if (dialog && shopPayDialogHasUserFocus(dialog, active) && active !== shopPayDialogMovedByUs) {
            shopPayDialogUserControl = active;
          }
          const elapsed = performance.now() - startedAt;
          tryFocus(elapsed >= SHOP_PAY_FOCUS_CORRECT_MS);
          if (elapsed >= SHOP_PAY_FOCUS_CORRECT_MS) {
            stop();
            return;
          }
          window.setTimeout(poll, 50);
        };
        window.setTimeout(poll, 0);
      },
      true
    );
  }

  // Shelfology Custom: after Okendo "Show more media", move focus to newly revealed items (WCAG 2.4.3).
  function visibleOkendoMediaItems(grid) {
    return Array.from(grid.querySelectorAll('.oke-mediaGrid-item')).filter(isElementDisplayed);
  }

  function focusableOkendoMediaTarget(item) {
    const nested = item.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (nested) return nested;
    if (!item.hasAttribute('tabindex')) item.setAttribute('tabindex', '-1');
    return item;
  }

  function bindOkendoMediaGridFocus() {
    if (bindOkendoMediaGridFocus.bound) return;
    bindOkendoMediaGridFocus.bound = true;

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest?.('.oke-mediaGrid-loadMore');
        if (!button) return;
        const grid = button.closest('[data-oke-media-grid], .oke-mediaGrid');
        if (!grid) return;

        const previous = visibleOkendoMediaItems(grid);
        const previousSet = new Set(previous);
        let attempts = 0;

        const moveFocus = () => {
          const items = visibleOkendoMediaItems(grid);
          const firstNew = items.find((item) => !previousSet.has(item));
          const targetItem = firstNew || items[previous.length];
          if (!targetItem) return false;
          focusableOkendoMediaTarget(targetItem).focus();
          return true;
        };

        const observer = new MutationObserver(() => {
          if (moveFocus()) observer.disconnect();
        });
        observer.observe(grid, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
        });

        const poll = () => {
          if (moveFocus()) {
            observer.disconnect();
            return;
          }
          attempts += 1;
          if (attempts < 40) window.setTimeout(poll, 50);
          else observer.disconnect();
        };
        window.setTimeout(poll, 50);
      },
      true
    );
  }

  bindShopPayLifecycle();
  bindOkendoMediaGridFocus();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onShopPayReady);
  } else {
    onShopPayReady();
  }
})();

/* Shelfology Custom: refresh section Prev/Next when zoom or resize changes scroll width.
   Heading .custom-carousel-controls groups hide when the track cannot scroll. */
(function () {
  const groupSelector = [
    '.custom-carousel-controls',
    '.title-wrapper > .indicators',
    '.featured-collections .tab-list > .indicators'
  ].join(', ');

  const groups = () => document.querySelectorAll(groupSelector);

  const sliderFor = (group) => {
    const button = group.querySelector('[aria-controls]');
    if (!button) return null;
    return document.getElementById(button.getAttribute('aria-controls'));
  };

  const canScroll = (slider) => slider.scrollWidth - slider.clientWidth > 4;

  // Inactive tab panels are display:none. Measuring that slider would freeze
  // both buttons as hidden. A group hidden only because the track cannot
  // scroll still needs a refresh so it can come back.
  const hiddenAncestor = (group) => {
    let node = group.parentElement;
    while (node) {
      if (node.hidden) return true;
      node = node.parentElement;
    }
    return false;
  };

  const shouldRefresh = (group) => {
    if (hiddenAncestor(group)) return false;
    return group.classList.contains('custom-carousel-controls') || !group.hidden;
  };

  const refreshButtons = (slider) => {
    if (!slider || !slider._sliderInitialized || typeof slider.updateButtons !== 'function') return;
    slider.updateButtons();
  };

  const syncGroup = (group) => {
    if (!group.classList.contains('custom-carousel-controls')) return;
    if (hiddenAncestor(group)) return;
    const slider = sliderFor(group);
    if (!slider) return;
    const hide = !canScroll(slider);
    if (hide && group.contains(document.activeElement) && typeof slider.focus === 'function') {
      slider.focus({ preventScroll: true });
    }
    group.hidden = hide;
  };

  const syncAll = () => {
    const seen = new Set();
    groups().forEach((group) => {
      if (!shouldRefresh(group)) return;
      const slider = sliderFor(group);
      if (!slider || seen.has(slider)) return;
      seen.add(slider);
      refreshButtons(slider);
    });
    groups().forEach(syncGroup);
  };

  const start = () => {
    syncAll();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(syncAll);
    const seen = new Set();
    groups().forEach((group) => {
      const slider = sliderFor(group);
      if (!slider || seen.has(slider)) return;
      seen.add(slider);
      observer.observe(slider);
      const grid = slider.querySelector('.card-grid');
      if (grid) observer.observe(grid);
      slider.addEventListener('load', syncAll, true);
      slider.addEventListener('scroll', syncAll, { passive: true });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  window.addEventListener('load', syncAll);
})();

// Shelfology Custom: Arrow keys on focused slider-element when the track scrolls.
(() => {
  const SliderElement = customElements.get('slider-element');
  if (!SliderElement?.prototype?.init) return;

  const originalInit = SliderElement.prototype.init;

  SliderElement.prototype.init = function patchedSliderElementInit() {
    originalInit.call(this);
    if (this._a11ySliderKeysBound) return;
    this._a11ySliderKeysBound = true;

    this.addEventListener('keydown', (event) => {
      if (event.target !== this) return;
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      if (!this.isScrollable) return;

      event.preventDefault();
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const goPrevious =
        (event.key === 'ArrowLeft' && !rtl) || (event.key === 'ArrowRight' && rtl);
      this.dispatchEvent(
        new CustomEvent(goPrevious ? 'slider:previous' : 'slider:next', {
          bubbles: true,
          cancelable: true,
        })
      );
    });
  };
})();
